import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from '@emotion/styled';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import { IconButton } from '@mui/material';
import useAuthStore from '../../store/authStore';
import LogoutButton from '../Auth/LogoutButton';
import { useThemeStore } from '../../store/themeStore';

const StyledHeader = styled.header`
  position: sticky;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  background: var(--bg-paper);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid var(--border-color);
  box-shadow: 0 15px 40px var(--shadow-color);
  z-index: 1000;
  width: 100%;
  box-sizing: border-box;
  height: var(--app-header-height);
  flex-shrink: 0;

  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
  }

  .app-brand {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 0.8;
    }

    .app-logo {
      height: 30px;
      width: auto;
      filter: var(--logo-filter);
      transition: filter 0.3s ease;
    }
    h1 {
      margin: 0;
      font-size: clamp(1.5rem, 2.2vw, 2rem);
      color: var(--text-primary);
    }
    span {
      font-size: 0.9rem;
      color: var(--primary-color);
      letter-spacing: 0.05em;
      text-transform: uppercase;
    }
  }

  .app-nav {
    display: flex;
    align-items: center;
    gap: 1rem;
    font-size: 0.95rem;

    @media (max-width: 768px) {
      display: none;
    }

    a {
      text-decoration: none;
      color: var(--primary-color);
      font-weight: 600;
      transition:
        color 0.2s ease,
        transform 0.2s ease;
      white-space: nowrap;
    }
    a:hover {
      color: var(--text-primary);
      transform: translateY(-1px);
    }
    .welcome-text {
      color: var(--text-primary);
      font-weight: 500;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 150px;

      @media (max-width: 1024px) {
        display: none;
      }
    }
    .auth-links {
      display: flex;
      gap: 0.85rem;
    }
  }

  .logout-btn {
    background: linear-gradient(135deg, #4f46e5, #6366f1);
    color: #fff;
    border: none;
    border-radius: 14px;
    padding: 0.55rem 1.3rem;
    cursor: pointer;
    font-weight: 600;
    box-shadow: 0 10px 25px rgba(99, 102, 241, 0.35);
    transition:
      transform 0.2s ease,
      box-shadow 0.2s ease;
  }

  .logout-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 15px 30px rgba(99, 102, 241, 0.4);
  }

  @media (max-width: 768px) {
    .logout-btn {
      width: 100%;
      padding: 0.75rem 1.3rem;
      text-align: center;
    }
  }
`;

const AdminButton = styled(Link)`
  background: linear-gradient(135deg, #f59e0b 0%, #f97316 100%);
  color: #fff !important;
  border: none;
  border-radius: 14px;
  padding: 0.55rem 1.3rem;
  cursor: pointer;
  font-weight: 600;
  text-decoration: none;
  display: inline-block;
  text-align: center;
  box-shadow: 0 10px 25px rgba(245, 158, 11, 0.35);
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    color: #fff !important;
    transform: translateY(-1px);
    box-shadow: 0 15px 30px rgba(245, 158, 11, 0.4);
  }

  @media (max-width: 768px) {
    width: 100%;
    padding: 0.75rem 1.3rem;
  }
`;

const ChatButton = styled(Link)`
  background: linear-gradient(135deg, #4f46e5, #6366f1);
  color: #fff !important;
  border: none;
  border-radius: 14px;
  padding: 0.55rem 1.3rem;
  cursor: pointer;
  font-weight: 600;
  text-decoration: none;
  display: inline-block;
  text-align: center;
  box-shadow: 0 10px 25px rgba(99, 102, 241, 0.35);
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    color: #fff !important;
    transform: translateY(-1px);
    box-shadow: 0 15px 30px rgba(99, 102, 241, 0.4);
  }

  @media (max-width: 768px) {
    width: 100%;
    padding: 0.75rem 1.3rem;
  }
`;

const MobileAuthLink = styled(Link)`
  background: linear-gradient(135deg, #4f46e5, #6366f1);
  color: #fff !important;
  border: none;
  border-radius: 14px;
  padding: 0.75rem 1.3rem;
  cursor: pointer;
  font-weight: 600;
  text-decoration: none;
  display: inline-block;
  text-align: center;
  box-shadow: 0 10px 25px rgba(99, 102, 241, 0.35);
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
  width: 100%;

  &:hover {
    color: #fff !important;
    transform: translateY(-1px);
    box-shadow: 0 15px 30px rgba(99, 102, 241, 0.4);
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: transparent;
  border: none;
  color: var(--primary-color);
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  min-height: 40px;

  @media (max-width: 768px) {
    display: flex;
  }

  &:active {
    background-color: var(--bg-app);
  }
`;

const MobileMenu = styled.div<{ isOpen: boolean }>`
  display: none;

  @media (max-width: 768px) {
    display: ${(props) => (props.isOpen ? 'flex' : 'none')};
    position: fixed;
    top: var(--app-header-height);
    left: 0;
    right: 0;
    background: var(--bg-paper);
    backdrop-filter: blur(20px);
    border-bottom: 1px solid var(--border-color);
    box-shadow: 0 10px 30px var(--shadow-color);
    flex-direction: column;
    padding: 1rem;
    gap: 1rem;
    z-index: 999;
    animation: slideDown 0.2s ease-out;

    @keyframes slideDown {
      from {
        opacity: 0;
        transform: translateY(-10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    .welcome-text {
      color: var(--text-primary);
      font-weight: 500;
      padding: 0.5rem;
      text-align: center;
      border-bottom: 1px solid var(--border-color);
    }

    .logout-btn {
      width: 100%;
      padding: 0.75rem 1.3rem;
      text-align: center;
      background: linear-gradient(135deg, #4f46e5, #6366f1);
      color: #fff;
      border: none;
      border-radius: 14px;
      cursor: pointer;
      font-weight: 600;
      box-shadow: 0 10px 25px rgba(99, 102, 241, 0.35);
      transition:
        transform 0.2s ease,
        box-shadow 0.2s ease;

      &:hover {
        transform: translateY(-1px);
        box-shadow: 0 15px 30px rgba(99, 102, 241, 0.4);
      }
    }
  }
`;

const MobileBackdrop = styled.div<{ isOpen: boolean }>`
  display: none;

  @media (max-width: 768px) {
    display: ${(props) => (props.isOpen ? 'block' : 'none')};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.3);
    z-index: 998;
  }
`;

const Header: React.FC = () => {
  const { isAuthenticated, user } = useAuthStore();
  const { mode, toggleTheme } = useThemeStore();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const isOnAdminPage = location.pathname.startsWith('/admin');

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <StyledHeader>
        <Link
          to="/chat"
          className="app-brand"
          style={{ textDecoration: 'none' }}
          onClick={closeMobileMenu}
        >
          <img src="/assets/ai-baze.svg" alt="AI Baze Analitika Logo" className="app-logo" />
        </Link>

        {/* Desktop Navigation */}
        <nav className="app-nav">
          <IconButton onClick={toggleTheme} color="primary" aria-label="Toggle theme">
            {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>
          {isAuthenticated ? (
            <>
              <span className="welcome-text">Welcome, {user?.username}!</span>
              {isOnAdminPage && <ChatButton to="/chat">Chat</ChatButton>}
              {!isOnAdminPage && user?.is_staff && (
                <AdminButton to="/admin">Admin Panel</AdminButton>
              )}
              <LogoutButton className="logout-btn" />
            </>
          ) : (
            <div className="auth-links">
              <Link to="/login">Login</Link>
              <Link to="/register">Register</Link>
            </div>
          )}
        </nav>

        {/* Mobile Menu Button */}
        <MobileMenuButton onClick={toggleMobileMenu} aria-label="Toggle menu">
          {isMobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
        </MobileMenuButton>
      </StyledHeader>

      {/* Mobile Menu */}
      <MobileBackdrop isOpen={isMobileMenuOpen} onClick={closeMobileMenu} />
      <MobileMenu isOpen={isMobileMenuOpen}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0.5rem' }}>
          <IconButton onClick={toggleTheme} color="primary" aria-label="Toggle theme">
            {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>
        </div>
        {isAuthenticated ? (
          <>
            <span className="welcome-text">Welcome, {user?.username}!</span>
            {isOnAdminPage && (
              <ChatButton to="/chat" onClick={closeMobileMenu}>
                Chat
              </ChatButton>
            )}
            {!isOnAdminPage && user?.is_staff && (
              <AdminButton to="/admin" onClick={closeMobileMenu}>
                Admin Panel
              </AdminButton>
            )}
            <div onClick={closeMobileMenu}>
              <LogoutButton className="logout-btn" />
            </div>
          </>
        ) : (
          <>
            <MobileAuthLink to="/login" onClick={closeMobileMenu}>
              Login
            </MobileAuthLink>
            <MobileAuthLink to="/register" onClick={closeMobileMenu}>
              Register
            </MobileAuthLink>
          </>
        )}
      </MobileMenu>
    </>
  );
};

export default Header;
